import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { Footer } from '../components/Footer';
import { Eye, TrendingUp, Zap } from 'lucide-react';
import { FloatingNav } from '../components/ui/FloatingNav';
import { HeroVisual } from '../components/ui/AbstractVisuals';
export function Hakkimizda() {
  const values = [{
    icon: Eye,
    title: 'Geleceği Tasarlamak',
    desc: 'Sadece bugünün sorunlarını değil, yarının ihtiyaçlarını da öngörüp çözmek.',
    color: 'text-neon-cyan'
  }, {
    icon: TrendingUp,
    title: 'Sürdürülebilir Büyüme',
    desc: 'Geçici trendler değil, kalıcı ve büyüyen dijital platformlar kurmak.',
    color: 'text-electric-violet'
  }, {
    icon: Zap,
    title: 'Hızlı Üretmek',
    desc: 'Fikirden ürüne giden en kısa yolu tasarlıyoruz.',
    color: 'text-neon-cyan'
  }];
  return <div className="min-h-screen bg-deep-dark text-white overflow-hidden" data-id="element-174">
      <FloatingNav data-id="element-175" />

      {/* SECTION 1: HERO */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-24 md:pt-20" data-id="element-176">
        {/* Background Glows */}
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-neon-cyan/10 rounded-full blur-[120px] pointer-events-none" data-id="element-177" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-electric-violet/10 rounded-full blur-[120px] pointer-events-none" data-id="element-178" />

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-179">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="text-left space-y-6" data-id="element-180">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight tracking-tight" data-id="element-181">
              Yarınları Kodluyor, <br data-id="element-182" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan to-electric-violet" data-id="element-183">
                Geleceği İnşa Ediyoruz.
              </span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed max-w-xl" data-id="element-184">
              VisionPlus Digital olarak biz, sadece teknoloji üretmiyoruz.
              Farklı disiplinleri bir araya getirerek, dokunduğu her sektörü
              dönüştüren sürdürülebilir bir gelecek tasarlıyoruz.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1
        }} className="relative flex justify-center" data-id="element-185">
            <HeroVisual data-id="element-186" />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: MANIFESTO & VALUES */}
      <section className="py-24 px-4 relative" data-id="element-187">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-188">
          {/* LEFT SIDE: Manifesto */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="sticky top-32" data-id="element-189">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-190">
              Kodun Ötesinde <br data-id="element-191" />
              <span className="text-neon-cyan" data-id="element-192">Bir Amaç.</span>
            </h2>
            <p className="text-xl text-gray-400 leading-relaxed" data-id="element-193">
              Teknoloji bizim için bir amaç değil, insan hayatına dokunmak için
              kullandığımız en güçlü araçtır. Her satır kodda şu soruyu sorarız:{' '}
              <span className="text-white font-medium" data-id="element-194">
                "Bu, birinin hayatını iyileştirecek mi?"
              </span>
            </p>
          </motion.div>

          {/* RIGHT SIDE: Values Cards */}
          <div className="space-y-6" data-id="element-195">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.6
          }} data-id="element-196">
                <GlassCard className="p-8 hover:bg-white/5 transition-colors group" data-id="element-197">
                  <div className="flex items-center gap-6" data-id="element-198">
                    <div className={`p-4 bg-white/5 rounded-xl ${value.color} group-hover:scale-110 transition-transform duration-300`} data-id="element-199">
                      <value.icon className="w-8 h-8" data-id="element-200" />
                    </div>
                    <div data-id="element-201">
                      <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-neon-cyan transition-colors" data-id="element-202">
                        {value.title}
                      </h3>
                      <p className="text-gray-400 text-lg" data-id="element-203">{value.desc}</p>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: TEAM SPIRIT */}
      <section className="py-24 px-4 relative overflow-hidden" data-id="element-204">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-electric-violet/5 to-transparent pointer-events-none" data-id="element-205" />

        <div className="max-w-4xl mx-auto text-center relative z-10 mb-12" data-id="element-206">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-207">
            Bizi Birleştiren: <br data-id="element-208" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan to-electric-violet" data-id="element-209">
              Ortak Tutku
            </span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto" data-id="element-210">
            Mühendisler ve hayalperestlerden oluşan multidisipliner bir ekip,
            imkansızı inşa etmek için birleşti.
          </motion.p>
        </div>

        {/* Abstract Network Visual */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1
      }} className="relative w-full max-w-5xl mx-auto h-[400px] flex items-center justify-center" data-id="element-211">
          <svg className="w-full h-full" viewBox="0 0 800 400" data-id="element-212">
            <defs data-id="element-213">
              <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%" data-id="element-214">
                <stop offset="0%" stopColor="#00D9FF" stopOpacity="0.8" data-id="element-215" />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" data-id="element-216" />
              </radialGradient>
            </defs>

            {/* Connecting Lines */}
            {[...Array(40)].map((_, i) => <motion.line key={`line-${i}`} x1={100 + i % 8 * 100} y1={50 + Math.floor(i / 8) * 80} x2={200 + i % 8 * 100} y2={100 + Math.floor(i / 8) * 80} stroke="url(#nodeGlow)" strokeWidth="1" opacity="0.2" initial={{
            pathLength: 0
          }} whileInView={{
            pathLength: 1
          }} transition={{
            duration: 2,
            delay: i * 0.02
          }} data-id="element-217" />)}

            {/* Glowing Nodes */}
            {[...Array(35)].map((_, i) => <motion.circle key={`node-${i}`} cx={100 + i % 7 * 120 + Math.random() * 50} cy={50 + Math.floor(i / 7) * 80 + Math.random() * 30} r={Math.random() * 4 + 2} fill={i % 2 === 0 ? '#00D9FF' : '#8B5CF6'} initial={{
            scale: 0,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: i * 0.03
          }} animate={{
            scale: [1, 1.5, 1],
            opacity: [0.4, 1, 0.4]
          }} transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} style={{
            filter: `drop-shadow(0 0 8px ${i % 2 === 0 ? '#00D9FF' : '#8B5CF6'})`
          }} data-id="element-218" />)}
          </svg>
        </motion.div>
      </section>

      <Footer data-id="element-219" />
    </div>;
}